import React, { useState, useEffect } from "react";
import MyPagination from "../../../components/pagination/Pagination";
import TableWithSkeleton from "../../../components/table/TableWithSkeleton";
import columns from "../../../columns/leaderboard";
import { GetLeaderboardApi } from "../../../api/request/leaderboard";

const LeaderboardMobile = () => {
  const [loading, setLoading] = useState(false);
  const [topTraders, setTopTraders] = useState([]);
  const [otherTraders, setOtherTraders] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecord, setTotalRecord] = useState(0);

  const getLeaderboard = async () => {
    try {
      setLoading(true);
      const params = {
        PageNumber: page,
        PageSize: pageSize,
      };
      const { data } = await GetLeaderboardApi(
        new URLSearchParams(params).toString()
      );

      if (data?.success) {
        setTotalRecord(data.totalRecords);

        const formattedData = data?.data?.map((item) => ({
          ...item,
          profitUI: (
            <div
              className={`flex justify-center items-center rounded-lg ${
                item.profit >= 0 ? "bg-[#E5F9F0]" : "bg-[#fff0ef]"
              }`}
            >
              <p
                className={`uppercase font-semibold text-sm py-1 px-2 ${
                  item.profit >= 0 ? "text-green-600" : "text-[#EB5950]"
                }`}
              >
                {item.profit}
              </p>
            </div>
          ),
          profitPercents: (
            <p
              className={`font-semibold ${
                item.profitPercent >= 0 ? "text-[#4D9F73]" : "text-[#EB5950]"
              }`}
            >
              {item.profitPercent}%
            </p>
          ),
          winRatios: (
            <p className="font-semibold text-black">{item.winRatio}%</p>
          ),
          pair: (
            <div className="bg-[#EAECF9] flex justify-center items-center rounded-lg">
              <p className="uppercase font-semibold text-[#4D5BAF] text-sm py-1 px-2">
                {item.pair || "-"}
              </p>
            </div>
          ),
          avgWins: (
            <p className="font-semibold text-[#42BE9E]">{item.avgWin}</p>
          ),
          avgLosses: (
            <p className="font-semibold text-[#EB5950]">{item.avgLoss}</p>
          ),
          trades: <p className="font-bold">{item.trades}</p>,
          losingStreak: (
            <p className="font-semibold text-[#EB5950]">{item.losingStreak}</p>
          ),
          winningStreaks: (
            <p className="font-semibold text-[#42BE9E]">{item.winningStreak}</p>
          ),
          totalLot: <p className="font-bold">{item.totalLots}</p>,
        }));

        setTopTraders(formattedData.slice(0, 3)); // top 3 ranks
        setOtherTraders(formattedData.slice(3)); // rest in table
      } else {
        setTopTraders([]);
        setOtherTraders([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getLeaderboard();
  }, [page, pageSize]);

  const SkeletonCard = () => (
    <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-5 animate-pulse">
      <div className="flex items-center gap-4 mb-4">
        <div className="w-14 h-12 bg-gray-200 rounded-full" />
        <div className="flex flex-col gap-2 w-2/3">
          <div className="h-4 bg-gray-200 rounded w-3/4" />
          <div className="h-3 bg-gray-100 rounded w-1/2" />
        </div>
        <div className="ml-auto h-5 w-16 bg-gray-200 rounded" />
      </div>
      <div className="flex justify-between w-full mt-auto">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex flex-col text-center gap-2">
            <div className="h-3 w-10 bg-gray-200 rounded" />
            <div className="h-4 w-8 bg-gray-300 rounded" />
          </div>
        ))}
      </div>
    </div>
  );

  return (
    <>
      {!loading && topTraders.length === 0 && otherTraders.length === 0 ? (
        <div className="flex flex-col justify-center items-center text-center h-screen">
          <img
            src="/assets/images/nodata.svg"
            alt="No data"
            className="w-20"
          />
          <h2 className="text-lg font-bold text-gray-700 mt-4">
            No Leaderboard Data Yet
          </h2>
          <p className="text-gray-400 text-sm mt-2">
            There is no data to show you right now
          </p>
        </div>
      ) : (
        <>
          {/* 🏆 Top 3 Traders as Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4 mb-5">
            {loading
              ? [1, 2, 3].map((i) => <SkeletonCard key={i} />)
              : topTraders.map((trader, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-xl p-4 flex flex-col gap-4 relative"
                  >
                    <div className="flex items-center gap-3">
                      <div
                        className={`flex-shrink-0 flex justify-center items-center px-4 py-3 rounded-full ${
                          index === 0
                            ? "bg-[#dab46054]"
                            : index === 1
                            ? "bg-slate-200"
                            : "bg-[#FFF0E1]"
                        }`}
                      >
                        <img
                          src={`/assets/images/rank${index + 1}.png`}
                          className="w-5"
                          alt={`Rank ${index + 1}`}
                        />
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-800 text-lg line-clamp-1">
                          {trader?.trader}
                        </h3>
                        <p className="text-xs text-gray-500">{trader?.login}</p>
                      </div>
                      <p className="ml-auto font-bold text-green-600 text-lg">
                        ${trader?.profit}
                      </p>
                    </div>

                    <div className="flex justify-between text-center mt-3">
                      <div>
                        <p className="text-xs text-gray-500">Profit %</p>
                        <p className="font-bold text-gray-800">
                          {trader?.profitPercent}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Avg Win</p>
                        <p className="font-bold text-gray-800">
                          {trader?.avgWin}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Avg Loss</p>
                        <p className="font-bold text-gray-800">
                          {trader?.avgLoss}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Total Lots</p>
                        <p className="font-bold text-gray-800">
                          {trader?.totalLots}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          {/* 📊 Table for 4th Rank onwards */}
          <div className="border border-gray-200 rounded-xl bg-white">
            <TableWithSkeleton
              columns={columns}
              data={otherTraders}
              loading={loading}
              pagination={false}
            />
          </div>

          <MyPagination
            total={totalRecord}
            pageSize={pageSize}
            setPageSize={setPageSize}
            page={page}
            setPage={setPage}
          />
        </>
      )}
    </>
  );
};

export default LeaderboardMobile;
